/* eslint-disable @typescript-eslint/no-use-before-define */

/* eslint-disable promise/avoid-new */
import type { DiscoveredService } from '#~/agent/discovery/utils.js';
import { wrapApiCall } from '#~/api/index.js';
import type { Discovery } from '#~/device/discovery/index.js';
import { ElectronAPI } from '#~/electron/index.js';
import { nats } from '#~/nats/index.js';
import {
  getRegistryRequestScope,
  scopedTemplateKey
} from '#~/device/registry/registry-config-cache.js';
import type { Agent } from '@studio/models/agent';
import type { Organization } from '@studio/models/organization';
import type { Room } from '@studio/models/room';
import type { User } from '@studio/models/user';
import {
  AgentDirectLinkingResponse,
  AgentDirectLinkStatusResponse
} from '@tryiris-ai/common.protojs/api/v1/agent_direct';
import type {
  AgentRegistryCreateInviteResponse,
  AgentRegistryGetAgentResponse,
  AgentRegistryBridgeSetupCapabilities,
  AgentRegistryDesktopBridgeCapacity,
  AgentRegistryGetOrganizationResponse,
  AgentRegistryGetRoomResponse,
  AgentRegistryEnsureInitialRoomResponse,
  AgentRegistryMembership,
  AgentRegistryLinkResponse
} from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import {
  AgentRegistryCreateAccountStudioOutcome,
  AgentRegistryDeleteRoomOutcome,
  AgentRegistryEnsureInitialRoomOutcome,
  AgentRegistryLinkPolicy
} from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import type { DeviceRegistryGetConfigurationResponse } from '@tryiris-ai/common.protojs/api/v1/device_registry';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import { transformArray } from '@tryiris-ai/js-types';
import {
  AgentRegistryCreateAccountStudio,
  AgentRegistryCreateOrganization,
  AgentRegistryCreateRoom,
  AgentRegistryEnsureInitialRoom,
  AgentRegistryDeleteRoom,
  AgentRegistryGet,
  AgentRegistryGetLinkRequest,
  AgentRegistryGetRoom,
  AgentRegistryLink,
  AgentRegistryList,
  AgentRegistryListInvites,
  AgentRegistryListMemberships,
  AgentRegistryListOrganizations,
  AgentRegistryListRooms,
  AgentRegistryUnlink,
  AgentRegistryUpdate
} from '@tryiris-ai/mailroom-helper/agentRegistry';
import { DeviceRegistryGetConfiguration } from '@tryiris-ai/mailroom-helper/deviceRegistry';
import type { OmitResponseProps } from '../types.js';
import { ApiError } from '../types.js';
import { toHexFromId, toIdFromHex } from '../utils.js';
import type { LinkRequest } from './types.js';
import { StatusCode } from 'status-code-enum';
import { initialBillingIsPending, rememberInitialBilling } from './initial-billing.js';
import { linkRequestDeadline } from './link-request-expiry.js';
import type { LinkRequestLifetime } from './link-request-expiry.js';
// Transport-level transient-retry now lives in the shared `#~/api/transient-retry.js`
// module so the agent-registry reads AND the boot-critical user fetch
// (auth/utils.ts fetchStudioUser) use ONE classifier + budget — see that file for
// the full p-retry/jest-ESM rationale and the 403-never-retried ENG-4154 contract.
// Re-exported here so existing importers (registry.transient-retry.test.ts) keep
// resolving `isTransientNetworkError` from this module.
// Per rule: think-in-systems + reference-rules-in-comments (ai.knowledge/global).
import { isTransientNetworkError, withTransientRetry } from '../transient-retry.js';

export { isTransientNetworkError };

/**
 * Creates an organization.
 *
 * @param name - Organization name.
 * @param token - Access token.
 * @returns Organization ID.
 */
export const createOrganization = async (name: string, token: string): Promise<Uint8Array> => {
  const result = await wrapApiCall(async () =>
    AgentRegistryCreateOrganization.request(nats.cloudConnection(), {
      name,
      token
    })
  );

  return result.id;
};

/**
 * Creates an organization with additional metadata.
 *
 * @param name - Organization name.
 * @param token - Access token.
 * @param metadata - Optional organization metadata.
 * @returns Organization ID.
 */
export const createOrganizationWithMetadata = async (
  // Currently not being used, however, will use in the future
  name: string,
  token: string,
  metadata?: {
    industry?: string;
    website?: string;
    profilePhotoUrl?: string;
  }
): Promise<Uint8Array> => {
  // First try to create with metadata if provided
  if (metadata && (metadata.industry || metadata.website || metadata.profilePhotoUrl)) {
    try {
      const result = await wrapApiCall(async () =>
        AgentRegistryCreateOrganization.request(nats.cloudConnection(), {
          name,
          token,
          ...(metadata.industry && { industry: metadata.industry }),
          ...(metadata.website && { website: metadata.website }),
          ...(metadata.profilePhotoUrl && { profilePhotoUrl: metadata.profilePhotoUrl })
        })
      );
      return result.id;
    } catch {
      // Fall back to basic creation if metadata fields are not supported
    }
  }

  // Fallback to basic organization creation
  return createOrganization(name, token);
};

/**
 * Result of {@link createRoom}: the room id plus whether the server DEDUPED this
 * create against an existing room. `alreadyExisted` lets the create flow tell
 * "linked to your existing Studio" apart from "new Studio created" (ENG-4209).
 * `id` is always present, so callers that only need the id destructure `.id` —
 * the previous id-only return contract is preserved.
 */
export interface CreateRoomResult {
  readonly id: Room['id'];
  readonly alreadyExisted: boolean;
}

/**
 * Pick the organization a keyless `createRoom` should target.
 *
 * ENG-4714: extracted so there is exactly ONE definition of "the user's
 * organization". Callers that must pin the org across several creates (the
 * create-studio wizard, the e2e probe) resolve it once through this function
 * and pass the result in, instead of re-deriving it per call and drifting onto
 * a different org mid-flow.
 *
 * Order comes from the server. `ListOrganizationsForUserID` sorts oldest-first
 * (api/common.api.database/queries/organization.go) so `[0]` is stable; before
 * that fix an org created between two calls could take the first slot and
 * silently redirect the second create.
 *
 * Falls back to minting an organization when the account has none that can
 * create rooms.
 */
export const resolveCreateRoomOrganizationId = async (
  token: string,
  fallbackName: string
): Promise<Uint8Array> => {
  const orgs = await listOrganizations(token);

  return (
    orgs.filter((org) => org.permissions?.canCreateRoom)[0]?.id ??
    (await createOrganization(fallbackName, token))
  );
};

/**
 * Creates a room.
 *
 * @param name - Room name.
 * @param token - Access token.
 * @param organizationId - Organization ID.
 * @param idempotencyKey - Stable per-attempt key (see api/agent/idempotency-key).
 *   Passing the SAME key on a retry of the same attempt makes the server return
 *   the already-created room (alreadyExisted=true) instead of provisioning a
 *   duplicate Studio (ENG-4209). Optional so existing id-only callers are
 *   unchanged.
 * @returns The room id and whether the room already existed.
 */
export const createRoom = async (
  name: string,
  token: string,
  organizationId?: Uint8Array,
  idempotencyKey?: string
): Promise<CreateRoomResult> => {
  const result = await createRoomDetailed(name, token, {
    ...(organizationId ? { organizationId } : {}),
    ...(idempotencyKey !== undefined ? { idempotencyKey } : {})
  });

  return {
    id: result.roomId,
    alreadyExisted: result.alreadyExisted
  };
};

export type CreateRoomDetailedOptions = {
  readonly organizationId?: Uint8Array;
  readonly idempotencyKey?: string;
};

export type CreateRoomDetailedResult = {
  readonly roomId: Room['id'];
  readonly alreadyExisted: boolean;
};

export type EnsureInitialRoomResult =
  | {
      readonly status: 'created' | 'same_request_existing';
      readonly roomId: Room['id'];
      readonly organizationId: Organization['id'];
      readonly roomCount: 1;
      readonly room: EnsureInitialRoomInventoryRoom;
      readonly organizationCount: number;
      readonly ownedOrganizations: readonly EnsureInitialRoomInventoryOrganization[];
      readonly capabilities: BridgeSetupCapabilities;
    }
  | {
      readonly status: 'inventory_changed';
      readonly roomCount: number;
      readonly rooms: readonly EnsureInitialRoomInventoryRoom[];
      readonly organizationCount: number;
      readonly ownedOrganizations: readonly EnsureInitialRoomInventoryOrganization[];
    };

export type EnsureInitialRoomInventoryRoom = {
  readonly initialBillingPending?: boolean | undefined;
  readonly id: Uint8Array;
  readonly name: string;
  readonly organizationId: Uint8Array;
  /** Committed Desktop Bridges only; reservations are counted in the capacity. */
  readonly activeBridges: {
    readonly id: Uint8Array;
    readonly name: string;
  }[];
  readonly canLinkBridge: boolean;
  /**
   * ENG-4483 W2: service-owned Desktop Bridge capacity. Omitted by servers
   * that predate it; the picker adapter treats omission as unknown, never as
   * unlimited (append-only wire contract).
   */
  readonly desktopBridgeCapacity?: AgentRegistryDesktopBridgeCapacity | undefined;
};

export type EnsureInitialRoomInventoryOrganization = {
  readonly id: Uint8Array;
  readonly name: string;
};

/**
 * ENG-4483 W3: what the service deployment supports for Bridge Setup. Read
 * from the authoritative inventory response; an older server omits the field
 * and every capability reads false. A UI flag never grants a capability.
 */
export type BridgeSetupCapabilities = {
  readonly desktopAdd: boolean;
  readonly durableSetup: boolean;
  /**
   * Setup rollback: rooms.delete honours `onlyIfUnlinked` under the link-admission
   * lock. Without it the picker never sends a rollback delete — an older
   * server would ignore the flag and delete unconditionally.
   */
  readonly setupRollback: boolean;
};

export type InspectInitialRoomInventoryResult = {
  readonly roomCount: number;
  readonly rooms: readonly EnsureInitialRoomInventoryRoom[];
  readonly organizationCount: number;
  readonly ownedOrganizations: readonly EnsureInitialRoomInventoryOrganization[];
  readonly capabilities: BridgeSetupCapabilities;
};

type AccountStudioInventoryWire = Pick<
  OmitResponseProps<AgentRegistryEnsureInitialRoomResponse>,
  'roomCount' | 'rooms' | 'organizationCount' | 'ownedOrganizations'
> & {
  // Only the ensure/inspect response advertises capabilities; the create
  // response shares the inventory shape without them.
  readonly capabilities?: AgentRegistryBridgeSetupCapabilities | undefined;
};

const readBridgeSetupCapabilities = (
  wire: AgentRegistryBridgeSetupCapabilities | undefined
): BridgeSetupCapabilities => ({
  desktopAdd: wire?.desktopAddV1 === true,
  durableSetup: wire?.durableSetupV1 === true,
  setupRollback: wire?.setupRollbackV1 === true
});

const readEnsureInitialRoomInventory = (
  result: AccountStudioInventoryWire
): InspectInitialRoomInventoryResult => {
  const rooms = result.rooms ?? [];
  const ownedOrganizations = result.ownedOrganizations ?? [];
  if (
    result.roomCount !== rooms.length ||
    result.organizationCount !== ownedOrganizations.length ||
    rooms.some(
      (room) =>
        toHexFromId<Room['id']>(room.id) === undefined ||
        toHexFromId<Organization['id']>(room.organizationId) === undefined ||
        room.activeBridges.some((bridge) => toHexFromId<Agent['id']>(bridge.id) === undefined)
    ) ||
    ownedOrganizations.some(
      (organization) => toHexFromId<Organization['id']>(organization.id) === undefined
    )
  ) {
    throw new Error('Initial Studio ensure returned an inconsistent authoritative inventory');
  }
  for (const room of rooms) {
    const roomId = toHexFromId<Room['id']>(room.id);
    if (roomId !== undefined) rememberInitialBilling(roomId, room.initialBillingPending);
  }
  return {
    roomCount: result.roomCount,
    rooms,
    organizationCount: result.organizationCount,
    ownedOrganizations,
    capabilities: readBridgeSetupCapabilities(result.capabilities)
  };
};

export type CreateAccountStudioResult = InspectInitialRoomInventoryResult & {
  readonly status: 'created' | 'same_request_existing';
  readonly roomId: Room['id'];
  readonly room: EnsureInitialRoomInventoryRoom;
};

/** Create one picker-requested Studio with server-owned organization routing. */
export const createAccountStudio = async (
  name: string,
  token: string,
  idempotencyKey: string
): Promise<CreateAccountStudioResult> => {
  const result = await wrapApiCall(async () =>
    AgentRegistryCreateAccountStudio.request(nats.cloudConnection(), {
      idempotencyKey,
      name,
      token
    })
  );

  const status =
    result.outcome ===
    AgentRegistryCreateAccountStudioOutcome.AGENT_REGISTRY_CREATE_ACCOUNT_STUDIO_OUTCOME_CREATED
      ? 'created'
      : result.outcome ===
          AgentRegistryCreateAccountStudioOutcome.AGENT_REGISTRY_CREATE_ACCOUNT_STUDIO_OUTCOME_SAME_REQUEST_EXISTING
        ? 'same_request_existing'
        : undefined;
  const inventory = readEnsureInitialRoomInventory(result);
  const roomId = toHexFromId<Room['id']>(result.id);
  const room = inventory.rooms.find(
    (candidate) => toHexFromId<Room['id']>(candidate.id) === roomId
  );
  if (status === undefined || roomId === undefined || room === undefined) {
    throw new Error('Account Studio create returned an invalid authoritative inventory');
  }
  if (result.initialBillingPending === true) rememberInitialBilling(roomId, true);
  return { status, roomId, room, ...inventory };
};

/**
 * Read account Studio cardinality from the primary without mutating.
 *
 * `fresh` bypasses the requester's ten-second response cache (and its
 * stale-on-error fallback). A read that follows a mutation — a Studio just
 * deleted or created — must reach the server, or the boot-time inventory is
 * replayed and the picker keeps showing a Studio that no longer exists.
 */
export const inspectInitialRoomInventory = async (
  token: string,
  options?: { readonly fresh?: boolean }
): Promise<InspectInitialRoomInventoryResult> => {
  const result = await wrapApiCall(async () =>
    AgentRegistryEnsureInitialRoom.request(
      nats.cloudConnection(),
      {
        idempotencyKey: '',
        inspectOnly: true,
        name: '',
        token
      },
      options?.fresh ? { force: true, staleOnError: false } : undefined
    )
  );
  if (
    result.outcome !==
      AgentRegistryEnsureInitialRoomOutcome.AGENT_REGISTRY_ENSURE_INITIAL_ROOM_OUTCOME_EMPTY &&
    result.outcome !==
      AgentRegistryEnsureInitialRoomOutcome.AGENT_REGISTRY_ENSURE_INITIAL_ROOM_OUTCOME_INVENTORY_CHANGED
  ) {
    throw new Error('Initial Studio inspection returned an invalid outcome');
  }
  const inventory = readEnsureInitialRoomInventory(result);
  const isEmpty =
    result.outcome ===
      AgentRegistryEnsureInitialRoomOutcome.AGENT_REGISTRY_ENSURE_INITIAL_ROOM_OUTCOME_EMPTY &&
    inventory.roomCount === 0;
  const hasInventory =
    result.outcome ===
      AgentRegistryEnsureInitialRoomOutcome.AGENT_REGISTRY_ENSURE_INITIAL_ROOM_OUTCOME_INVENTORY_CHANGED &&
    inventory.roomCount > 0;
  if (!isEmpty && !hasInventory) {
    throw new Error('Initial Studio inspection returned an invalid outcome');
  }
  return inventory;
};

/**
 * Atomically ensures the authenticated account's first Studio on the primary
 * database. This endpoint deliberately has no legacy create fallback: an old
 * backend must fail closed instead of recreating the cross-machine duplicate.
 */
export const ensureInitialRoom = async (
  name: string,
  token: string,
  idempotencyKey: string
): Promise<EnsureInitialRoomResult> => {
  const result = await wrapApiCall(async () =>
    AgentRegistryEnsureInitialRoom.request(nats.cloudConnection(), {
      idempotencyKey,
      inspectOnly: false,
      name,
      token
    })
  );

  if (
    result.outcome !==
      AgentRegistryEnsureInitialRoomOutcome.AGENT_REGISTRY_ENSURE_INITIAL_ROOM_OUTCOME_CREATED &&
    result.outcome !==
      AgentRegistryEnsureInitialRoomOutcome.AGENT_REGISTRY_ENSURE_INITIAL_ROOM_OUTCOME_SAME_REQUEST_EXISTING &&
    result.outcome !==
      AgentRegistryEnsureInitialRoomOutcome.AGENT_REGISTRY_ENSURE_INITIAL_ROOM_OUTCOME_INVENTORY_CHANGED
  ) {
    throw new Error('Initial Studio ensure returned an invalid outcome');
  }
  const inventory = readEnsureInitialRoomInventory(result);

  if (
    result.outcome ===
    AgentRegistryEnsureInitialRoomOutcome.AGENT_REGISTRY_ENSURE_INITIAL_ROOM_OUTCOME_INVENTORY_CHANGED
  ) {
    if (inventory.roomCount < 1) {
      throw new Error('Initial Studio inventory changed without an authoritative inventory');
    }
    return {
      status: 'inventory_changed',
      ...inventory
    };
  }

  const status =
    result.outcome ===
    AgentRegistryEnsureInitialRoomOutcome.AGENT_REGISTRY_ENSURE_INITIAL_ROOM_OUTCOME_CREATED
      ? 'created'
      : result.outcome ===
          AgentRegistryEnsureInitialRoomOutcome.AGENT_REGISTRY_ENSURE_INITIAL_ROOM_OUTCOME_SAME_REQUEST_EXISTING
        ? 'same_request_existing'
        : undefined;
  const roomId = toHexFromId<Room['id']>(result.id);
  const organizationId = toHexFromId<Organization['id']>(result.organizationId);
  const authoritativeRoom = inventory.rooms[0];
  if (
    !status ||
    !roomId ||
    !organizationId ||
    result.roomCount !== 1 ||
    inventory.rooms.length !== 1 ||
    authoritativeRoom === undefined ||
    toHexFromId<Room['id']>(authoritativeRoom.id) !== roomId ||
    toHexFromId<Organization['id']>(authoritativeRoom.organizationId) !== organizationId ||
    result.organizationCount !== inventory.ownedOrganizations.length
  ) {
    throw new Error('Initial Studio ensure returned an invalid outcome');
  }
  if (result.initialBillingPending === true) rememberInitialBilling(roomId, true);

  return {
    status,
    roomId,
    organizationId,
    roomCount: 1,
    room: authoritativeRoom,
    organizationCount: inventory.organizationCount,
    ownedOrganizations: inventory.ownedOrganizations,
    capabilities: inventory.capabilities
  };
};

/** Classify direct room entry on primary, then repair only that owned Studio.
 * Inspection also covers a fresh browser session with no local setup receipt. */
export const resolveInitialRoomBilling = async (
  roomId: Room['id'],
  token: string
): Promise<boolean> => {
  const inventory = await inspectInitialRoomInventory(token, { fresh: true });
  const room = inventory.rooms.find(
    (candidate) => toHexFromId<Room['id']>(candidate.id) === roomId
  );
  if (room === undefined) throw new Error('Studio is no longer accessible');
  const owned = inventory.ownedOrganizations.some(
    (org) =>
      toHexFromId<Organization['id']>(org.id) ===
      toHexFromId<Organization['id']>(room.organizationId)
  );
  if (!owned) {
    // Primary says this account has no setup to finish here. Drop any receipt for this
    // room so a later inspection failure cannot block entry on another account's billing.
    rememberInitialBilling(roomId, false);
    return false;
  }
  const result = await wrapApiCall(async () =>
    AgentRegistryEnsureInitialRoom.request(
      nats.cloudConnection(),
      {
        token,
        name: '',
        idempotencyKey: '',
        inspectOnly: false,
        billingRoomId: room.id
      },
      { force: true, staleOnError: false }
    )
  );
  const repairedRoom = result.rooms.find(
    (candidate) => toHexFromId<Room['id']>(candidate.id) === roomId
  );
  if (repairedRoom === undefined)
    throw new Error('Studio billing repair returned no selected Studio');
  rememberInitialBilling(roomId, repairedRoom.initialBillingPending);
  if (result.initialBillingPending === true) rememberInitialBilling(roomId, true);
  return initialBillingIsPending(roomId);
};

/**
 * Creates a room while preserving the generated registry idempotency result.
 * Existing callers keep the id-only {@link createRoom} contract; zero-only
 * auto-link uses this seam to avoid deleting a room recovered by a retry.
 */
export const createRoomDetailed = async (
  name: string,
  token: string,
  options: CreateRoomDetailedOptions = {}
): Promise<CreateRoomDetailedResult> => {
  // ENG-4714: one definition of "the user's organization" — resolve through
  // the shared helper so wizard/probe callers that pin an org and this keyless
  // path cannot drift onto different orgs mid-flow.
  const safeOrgId = options.organizationId ?? (await resolveCreateRoomOrganizationId(token, name));

  const result = await wrapApiCall(async () =>
    AgentRegistryCreateRoom.request(nats.cloudConnection(), {
      name,
      organizationId: safeOrgId!,
      token,
      ...(options.idempotencyKey !== undefined ? { idempotencyKey: options.idempotencyKey } : {})
    })
  );

  // Keep the committed identity even for keyless callers. Room entry repairs it.
  if (result.initialBillingPending === true)
    rememberInitialBilling(toHexFromId<Room['id']>(result.id)!, true);

  return {
    roomId: toHexFromId<Room['id']>(result.id)!,
    // Nullish-default: protojs versions predating the field (or a server that
    // omits it) read as "created" rather than crashing the flow (ENG-4209).
    alreadyExisted: result.alreadyExisted ?? false
  };
};

/**
 * Deletes a room.
 *
 * @param roomId - Room ID.
 * @param token - Access token.
 */
export const deleteRoom = async (roomId: Room['id'], token: string): Promise<void> => {
  await wrapApiCall(async () =>
    AgentRegistryDeleteRoom.request(nats.cloudConnection(), {
      id: toIdFromHex(roomId)!,
      token
    })
  );
};

/**
 * `kept_reserved`: only a mid-handshake link reservation holds the Studio.
 * That reservation can still roll back, so callers keep their recovery record
 * and try again later instead of treating the Studio as claimed.
 */
/**
 * `kept_billing_incomplete`: the server refused because not every Stripe id
 * is persisted on the Studio, so a cancel could not reach every object. The
 * Studio stays live and linkable; this is terminal, not a retry.
 */
export type DeleteUnlinkedRoomOutcome =
  | 'deleted'
  | 'kept_occupied'
  | 'kept_reserved'
  | 'kept_billing_incomplete';

/**
 * setup rollback: delete a Studio ONLY if no Desktop Bridge (committed
 * or provisional) is attached. The server decides under the same Studio row
 * lock link admission takes, so a link landing concurrently can never lose
 * its Studio; `kept_occupied` is that refusal. Callers gate on
 * {@link BridgeSetupCapabilities.setupRollback} first: a server that predates
 * the flag deletes unconditionally and answers without an outcome, which is
 * surfaced here as an error rather than mistaken for success.
 *
 * @param roomId - Room ID.
 * @param token - Access token.
 * @returns Whether the Studio was deleted or kept because a Bridge holds it.
 */
export const deleteUnlinkedRoom = async (
  roomId: Room['id'],
  token: string
): Promise<DeleteUnlinkedRoomOutcome> => {
  const result = await wrapApiCall(async () =>
    AgentRegistryDeleteRoom.request(nats.cloudConnection(), {
      id: toIdFromHex(roomId)!,
      onlyIfUnlinked: true,
      token
    })
  );
  switch (result.outcome) {
    case AgentRegistryDeleteRoomOutcome.AGENT_REGISTRY_DELETE_ROOM_OUTCOME_DELETED:
      return 'deleted';
    case AgentRegistryDeleteRoomOutcome.AGENT_REGISTRY_DELETE_ROOM_OUTCOME_KEPT_OCCUPIED:
      return 'kept_occupied';
    case AgentRegistryDeleteRoomOutcome.AGENT_REGISTRY_DELETE_ROOM_OUTCOME_KEPT_RESERVED:
      return 'kept_reserved';
    case AgentRegistryDeleteRoomOutcome.AGENT_REGISTRY_DELETE_ROOM_OUTCOME_KEPT_BILLING_INCOMPLETE:
      return 'kept_billing_incomplete';
    default:
      throw new Error('Conditional Studio delete returned no outcome; the server may predate it.');
  }
};

/**
 * Fetch the details about the specified agent.
 *
 * @param agentId - Agent ID.
 * @param token - Access token.
 * @returns Agent details.
 */
export const fetchAgent = async (
  agentId: Agent['id'],
  token: string
): Promise<Omit<Agent, 'status'>> =>
  // Idempotent read (agents.get) — wrap in the transport transient retry. A 403
  // (cross-account) is NOT retried; it surfaces immediately. See
  // withTransientRetry / isTransientNetworkError above.
  withTransientRetry(async () =>
    wrapApiCall(async () => {
      const agent = await AgentRegistryGet.request(nats.cloudConnection(), {
        id: toIdFromHex(agentId)!,
        token
      });

      const result = {
        ...agent,
        id: agentId,
        roomId: toHexFromId<Room['id']>(agent.roomId)!
      };

      const { modelId } = agent;

      if (modelId !== undefined) {
        const config = await wrapApiCall(async () =>
          DeviceRegistryGetConfiguration.request(nats.cloudConnection(), {
            modelId,
            token
          })
        );

        return {
          ...result,
          iconUrl: config.model?.iconUrl
        };
      }

      return result;
    })
  );

/**
 * Fetch the details about all agents associated with a room — STRICT variant:
 * a failed read REJECTS instead of resolving `{}`.
 *
 * Why the distinction exists: a rejected GetRoom and a room that settled with
 * zero agents are different answers, but the lenient `fetchAgentsForRoom`
 * flattens both to `{}`. On the boot path that ambiguity is a product defect —
 * `useRoomAgentStatus` opened the `<AgentProvider>` gate with an empty depot,
 * and after `EMPTY_STUDIO_GRACE_MS` the room readiness machine concluded
 * "bridge-less Studio", published `boot.isRootReady=true`, and dismissed the
 * BootOverlay onto the `<Linker>` ("First things first") for a Studio that HAS
 * a linked Bridge, with no in-context recovery (only the boot enumeration ever
 * ADDS depot agents). That is the CI rapid-refresh failure family
 * (studio-refresh.spec.ts:161); the transport-retry-only attempt (PR #1077)
 * was falsified at its exact SHA because the failing error class is not
 * transient-classified. Boot-readiness decisions MUST use this variant.
 * Per rule: reference-rules-in-comments + think-in-systems (ai.knowledge/global).
 *
 * @param roomId - Room ID.
 * @param token - Access token.
 * @param force - Force fetch the data (bypass the settled-result cache).
 *
 * @returns Agent details; `{}` means the server answered "zero agents".
 * @throws The original transport/API error when the read did not settle.
 */
export const fetchAgentsForRoomStrict = async (
  roomId: Room['id'],
  token: string,
  force: boolean = false
): Promise<Record<Agent['id'], Omit<Agent, 'status'>>> => {
  // Check if we have a cached result. Only SETTLED results are ever cached, so
  // a cache hit is always a real server answer, never a masked failure. A
  // cached list that still lacks a just-claimed agent is stale, not an answer.
  if (
    !force &&
    roomCache.has(roomId) &&
    missingExpectedAgent(roomId, roomCache.get(roomId)!) === undefined
  ) {
    return roomCache.get(roomId)!;
  }

  // Join a request already in flight. Waiters carry BOTH settle paths so a
  // failure propagates to every joiner as a rejection — resolving joiners with
  // `{}` would silently recreate the failure/empty ambiguity for them.
  if (roomInFlight.has(roomId)) {
    return new Promise<Record<Agent['id'], Omit<Agent, 'status'>>>((resolve, reject) => {
      roomInFlight.get(roomId)!.push({ reject, resolve });
    });
  }

  // Otherwise, fetch the data
  const results: Record<Agent['id'], Omit<Agent, 'status'>> = {};

  // Add a callback to the in-flight map
  roomInFlight.set(roomId, []);

  const settleRoomInFlight = (settle: (waiter: RoomInFlightWaiter) => void) => {
    const waiters = roomInFlight.get(roomId)!;

    // Remove the waiters from the in-flight map before settling them.
    roomInFlight.delete(roomId);

    for (const waiter of waiters) {
      settle(waiter);
    }
  };

  // Fetch the data. Deliberately NO catch-to-null here: the failure must reach
  // the caller (and every in-flight joiner) as a rejection.
  let room: T_Maybe<Awaited<ReturnType<typeof fetchRoom>>>;

  try {
    room = await fetchRoom(roomId, token);
  } catch (cause) {
    settleRoomInFlight((waiter) => waiter.reject(cause));
    throw cause;
  }

  if (!room) {
    // Defensive: a nullish payload from a resolved call settles as an empty
    // room (the server answered), matching the pre-split behavior.
    settleRoomInFlight((waiter) => waiter.resolve({}));
    return {};
  }

  const { agents } = room;

  // Transform the data
  for (const agent of agents) {
    const id = toHexFromId(agent.id) as Agent['id'] | undefined;

    if (!id) {
      continue;
    }

    results[id] = {
      ...agent,
      id,
      roomId
    };
  }

  // Cache the data, unless it still lacks a just-claimed agent: caching that
  // list would serve the pre-claim room to every reader for ROOM_CACHE_TTL.
  if (missingExpectedAgent(roomId, results) === undefined) {
    roomCache.set(roomId, results);

    // Clear the cache after a TTL period
    setTimeout(() => {
      roomCache.delete(roomId);
    }, ROOM_CACHE_TTL);
  }
  settleRoomInFlight((waiter) => waiter.resolve(results));

  // Return the results
  return results;
};

/** @internal Single GetRoom read, shared by the strict enumeration above. */
const fetchRoom = async (roomId: Room['id'], token: string) =>
  wrapApiCall(async () =>
    AgentRegistryGetRoom.request(nats.cloudConnection(), {
      id: toIdFromHex(roomId)!,
      token
    })
  );

/**
 * Fetch the details about all agents associated with a room — LENIENT variant:
 * resolves `{}` on ANY failure (byte-compatible with the historical behavior).
 *
 * Safe only where "unknown" may degrade to "none" without a wrong decision
 * being locked in: billing limit checks, presence wiring, and link flows that
 * force-refetch. Boot-path readiness MUST use `fetchAgentsForRoomStrict` —
 * see its doc comment for the rapid-refresh Linker defect this split fixes.
 *
 * @param roomId - Room ID.
 * @param token - Access token.
 * @param force - Force fetch the data.
 *
 * @returns Agent details; `{}` on a settled-empty room OR any failure.
 */
export const fetchAgentsForRoom = async (
  roomId: Room['id'],
  token: string,
  force: boolean = false
): Promise<Record<Agent['id'], Omit<Agent, 'status'>>> =>
  fetchAgentsForRoomStrict(roomId, token, force).catch(() => ({}));

const ROOM_CACHE_TTL = 1000 * 60; // 1 minute
const roomCache = new Map<Room['id'], Record<Agent['id'], Omit<Agent, 'status'>>>();

/**
 * Claimed-camera expectation. The claim screen links a camera and lands in the
 * room within seconds, but `rooms.get` reads a database replica that can lag
 * the link write by several seconds. The claim screen's settle poll
 * (routes/agent/link/link-settle.ts) gives up after ~5s and navigates anyway,
 * and the room then built its cameras from a list without the new one and
 * never read it again, so the claimed camera stayed missing until a reload
 * (HIL run 37958378161: a real Bolin claim, `rooms.get` caught up ~0.5s after
 * the room had already loaded from cache).
 *
 * While an expectation is live for a room, a list that lacks the agent is
 * neither served from nor written to the cache, and
 * `fetchAgentsForRoomAwaitingExpected` keeps re-reading until it appears or
 * the deadline passes. The deadline keeps a camera that never appears from
 * holding the room hostage; after it, every read behaves exactly as before.
 */
export const EXPECTED_AGENT_TTL_MS = 30_000;
export const EXPECTED_AGENT_RETRY_MS = 1_000;
const expectedAgents = new Map<Room['id'], { agentId: Agent['id']; deadline: number }>();

/** Record that `agentId` was just linked into `roomId` and must appear in its agent list. */
export const expectAgentInRoom = (
  roomId: Room['id'],
  agentId: Agent['id'],
  ttlMs: number = EXPECTED_AGENT_TTL_MS
): void => {
  expectedAgents.set(roomId, { agentId, deadline: Date.now() + ttlMs });
};

/**
 * The live expected agent this list is missing, if any. Ends the expectation
 * once the agent is present or the deadline has passed.
 */
const missingExpectedAgent = (
  roomId: Room['id'],
  agents: Record<Agent['id'], unknown>
): Agent['id'] | undefined => {
  const expected = expectedAgents.get(roomId);
  if (expected === undefined) {
    return undefined;
  }
  if (expected.agentId in agents || Date.now() >= expected.deadline) {
    expectedAgents.delete(roomId);
    return undefined;
  }
  return expected.agentId;
};

/**
 * `fetchAgentsForRoom` for the read that builds the room's cameras: when a
 * camera was just claimed into this room, wait (bounded by the expectation's
 * deadline) until the server lists it. Without an expectation this is a single
 * `fetchAgentsForRoom` call.
 *
 * @param roomId - Room ID.
 * @param token - Access token.
 *
 * @returns Agent details; `{}` on a settled-empty room OR any failure.
 */
export const fetchAgentsForRoomAwaitingExpected = async (
  roomId: Room['id'],
  token: string
): Promise<Record<Agent['id'], Omit<Agent, 'status'>>> => {
  for (;;) {
    const agents = await fetchAgentsForRoom(roomId, token);
    if (missingExpectedAgent(roomId, agents) === undefined) {
      return agents;
    }
    await new Promise((resolve) => setTimeout(resolve, EXPECTED_AGENT_RETRY_MS));
  }
};

type RoomInFlightWaiter = {
  resolve: (result: Record<Agent['id'], Omit<Agent, 'status'>>) => void;
  reject: (cause: unknown) => void;
};

const roomInFlight = new Map<Room['id'], RoomInFlightWaiter[]>();

const modelConfigsLoaded = new Map<
  string,
  OmitResponseProps<DeviceRegistryGetConfigurationResponse>
>();

/** Clears loaded model config entries (e.g. when active org changes). */
export const clearModelConfigCache = (): void => {
  modelConfigsLoaded.clear();
};

const modelConfigCacheKey = (modelId: Uint8Array): string => {
  const hex = toHexFromId(modelId)!;
  return scopedTemplateKey(getRegistryRequestScope(), hex);
};
/*
 * ENG-4176: waiters carry BOTH settle functions (mirroring roomInFlight above).
 * The previous resolve-only callback list combined with a success-only
 * `delete(key)` meant one rejected fetch marooned every queued same-model
 * caller forever — resolve never fired, the key stayed occupied, and every
 * later call for that model joined a queue nobody would ever settle (silent
 * hang until reload). Per reference-rules-in-comments.md.
 */
type ModelConfigWaiter = {
  resolve: (config: OmitResponseProps<DeviceRegistryGetConfigurationResponse>) => void;
  reject: (cause: unknown) => void;
};

const modelConfigsLoadingCallbacks = new Map<string, ModelConfigWaiter[]>();

/**
 * Fetch the configuration for the specified model.
 *
 * @param modelId - Model ID.
 * @param token - Access token.
 * @returns Model configuration.
 */
export const fetchModelConfig = async (
  modelId: Uint8Array,
  token: string
): Promise<OmitResponseProps<DeviceRegistryGetConfigurationResponse>> => {
  const key = modelConfigCacheKey(modelId);

  if (modelConfigsLoaded.has(key)) {
    return modelConfigsLoaded.get(key)!;
  }

  if (modelConfigsLoadingCallbacks.has(key)) {
    return new Promise<OmitResponseProps<DeviceRegistryGetConfigurationResponse>>(
      (resolve, reject) => {
        modelConfigsLoadingCallbacks.get(key)!.push({ reject, resolve });
      }
    );
  }

  modelConfigsLoadingCallbacks.set(key, []);

  // Detach the waiter list and settle everyone with one outcome — the map
  // entry is removed BEFORE settling so a retry issued from a rejection
  // handler starts a fresh fetch instead of joining the dead queue (ENG-4176).
  const settleModelConfigWaiters = (settle: (waiter: ModelConfigWaiter) => void) => {
    const waiters = modelConfigsLoadingCallbacks.get(key)!;

    modelConfigsLoadingCallbacks.delete(key);

    for (const waiter of waiters) {
      settle(waiter);
    }
  };

  let config: OmitResponseProps<DeviceRegistryGetConfigurationResponse>;

  try {
    config = await wrapApiCall(async () =>
      DeviceRegistryGetConfiguration.request(nats.cloudConnection(), {
        modelId,
        token
      })
    );
  } catch (cause) {
    // The failure must reach the initiating caller AND every queued joiner as
    // a rejection — swallowing here would recreate the silent-hang class.
    settleModelConfigWaiters((waiter) => waiter.reject(cause));
    throw cause;
  }

  modelConfigsLoaded.set(key, config);
  settleModelConfigWaiters((waiter) => waiter.resolve(config));

  return config;
};

/**
 * Fetch information about the agent associated with the specified link request.
 *
 * @param requestId - The link request ID.
 * @param token - Access token.
 * @returns Agent name and version.
 */
export const fetchRequestLink = async (
  requestId: LinkRequest['id'],
  token: string
): Promise<Omit<Agent, 'id' | 'lastShutDownAt' | 'roomId' | 'status'> & LinkRequestLifetime> => {
  const agent = await wrapApiCall(async () =>
    AgentRegistryGetLinkRequest.request(nats.cloudConnection(), {
      id: toIdFromHex(requestId)!
    })
  );

  // Capture receipt before the optional model lookup so that lookup time counts.
  const linkExpiresAt = linkRequestDeadline(agent);
  const { modelId } = agent;

  if (modelId !== undefined) {
    const config = await wrapApiCall(async () =>
      DeviceRegistryGetConfiguration.request(nats.cloudConnection(), {
        modelId,
        token
      })
    );

    return {
      ...agent,
      linkExpiresAt,
      iconUrl: config.model?.iconUrl
    };
  }

  return { ...agent, linkExpiresAt };
};

/**
 * Fetch the room associated with the specified agent.
 *
 * @private
 * @param agentId - Agent ID.
 * @param token - Access token.
 * @returns Room id.
 */
export const fetchRoomIdForAgentId = async (
  agentId: Agent['id'],
  token: string
): Promise<T_Maybe<Room['id']>> => {
  // Idempotent read (agents.get). Wrapped in the transport transient retry, but
  // a 403 is NEVER retried: checkBridgeLinkedRoom (auto-link-room.ts:55-64) maps
  // the FIRST 403 to forbidden:true and the controller's transient-403 re-probe
  // (auto-link-controller.ts) discriminates transient-vs-cross-account on TIME.
  // Retrying the 403 here would mask that signal and regress ENG-4154.
  const agent = (await withTransientRetry(async () =>
    wrapApiCall(async () =>
      AgentRegistryGet.request(nats.cloudConnection(), {
        id: toIdFromHex(agentId)!,
        token
      })
    )
  )) as AgentRegistryGetAgentResponse;

  return toHexFromId<Room['id']>(agent.roomId);
};

/**
 * Fetch the ENG-4037 remembered-Studio preference for an agent (bridge).
 *
 * @param agentId - Agent ID.
 * @param token - Access token.
 * @returns Remembered room id, or undefined when no preference is set.
 */
export const fetchRememberedStudioForAgent = async (
  agentId: Agent['id'],
  linkRequestId: LinkRequest['id'],
  token: string
): Promise<T_Maybe<Room['id']>> => {
  // Same idempotent agents.get read as fetchRoomIdForAgentId — the preference
  // rides the existing endpoint (no new lambda surface).
  const agent = (await withTransientRetry(async () =>
    wrapApiCall(async () =>
      AgentRegistryGet.request(nats.cloudConnection(), {
        id: toIdFromHex(agentId)!,
        linkRequestId: toIdFromHex(linkRequestId)!,
        token
      })
    )
  )) as AgentRegistryGetAgentResponse;

  return agent.rememberedRoomId?.length
    ? toHexFromId<Room['id']>(agent.rememberedRoomId)
    : undefined;
};

/**
 * Set or clear the ENG-4037 remembered-Studio preference for an agent.
 *
 * @param agentId - Agent ID.
 * @param roomId - Room to remember, or undefined to CLEAR the preference
 *   (sent as empty bytes — the wire contract for "clear").
 * @param token - Authentication token.
 */
export const setRememberedStudioForAgent = async (
  agentId: Agent['id'],
  roomId: T_Maybe<Room['id']>,
  token: string
): Promise<void> => {
  await wrapApiCall(async () =>
    AgentRegistryUpdate.request(nats.cloudConnection(), {
      id: toIdFromHex(agentId)!,
      rememberedRoomId: roomId ? toIdFromHex(roomId)! : new Uint8Array(0),
      token
    })
  );
};

/**
 * Fetch a room by ID.
 *
 * @param roomId - Room ID.
 * @param token - Access token.
 * @returns Room response or undefined if not found.
 */
export const fetchRoomById = async (
  roomId: Room['id'],
  token: string
): Promise<T_Maybe<AgentRegistryGetRoomResponse>> => {
  try {
    // Idempotent read (getRoom) — transport transient retry. 404 is
    // non-transient so it is NOT retried; it falls straight through to the
    // catch below, which returns undefined (semantics unchanged).
    const room = await withTransientRetry(async () =>
      wrapApiCall(async () =>
        AgentRegistryGetRoom.request(nats.cloudConnection(), {
          id: toIdFromHex(roomId)!,
          token
        })
      )
    );

    return room as AgentRegistryGetRoomResponse;
  } catch (error) {
    if (error instanceof ApiError && error.code === StatusCode.ClientErrorNotFound) {
      return undefined;
    }

    throw error;
  }
};

/**
 * Establish a link with the agent.
 *
 * @param agentId - Agent ID.
 * @param requestId - Link request ID.
 * @param roomId - Room ID.
 * @param token - Access token.
 * @param username - Username.
 * @param password - Password.
 */
export const linkAgent = async (
  agentId: Agent['id'],
  requestId: LinkRequest['id'],
  roomId: Room['id'],
  token: string,
  username?: string,
  password?: string,
  options: {
    readonly policy?: 'require_available' | 'replace_existing' | 'add_existing';
    readonly expectedDisplacedAgentIds?: readonly Agent['id'][];
    // ENG-4483 W3: the confirmed committed Desktop set for 'add_existing'.
    readonly expectedExistingAgentIds?: readonly Agent['id'][];
  } = {}
): Promise<void> => {
  const linkPolicy =
    options.policy === 'require_available'
      ? AgentRegistryLinkPolicy.AGENT_REGISTRY_LINK_POLICY_REQUIRE_AVAILABLE
      : options.policy === 'replace_existing'
        ? AgentRegistryLinkPolicy.AGENT_REGISTRY_LINK_POLICY_REPLACE_EXISTING
        : options.policy === 'add_existing'
          ? AgentRegistryLinkPolicy.AGENT_REGISTRY_LINK_POLICY_ADD_EXISTING
          : AgentRegistryLinkPolicy.AGENT_REGISTRY_LINK_POLICY_UNSPECIFIED;
  await wrapApiCall(async () =>
    AgentRegistryLink.request(nats.cloudConnection(), {
      agentId: toIdFromHex(agentId)!,
      requestId: toIdFromHex(requestId)!,
      roomId: toIdFromHex(roomId)!,
      token,
      linkPolicy,
      expectedDisplacedAgentIds: (options.expectedDisplacedAgentIds ?? []).map(
        (id) => toIdFromHex(id)!
      ),
      expectedExistingAgentIds: (options.expectedExistingAgentIds ?? []).map(
        (id) => toIdFromHex(id)!
      ),
      ...(username && { username }),
      ...(password && { password })
    })
  );
};

/**
 * Establish a link with an agent that requires device authentication.
 *
 * @param agentId - Agent ID.
 * @param requestId - Link request ID.
 * @param roomId - Room ID.
 * @param token - Access token.
 * @param username - Username.
 * @param password - Password.
 */
export const linkAgentWithAuth = async (
  agentId: Agent['id'],
  requestId: LinkRequest['id'],
  roomId: Room['id'],
  token: string,
  username?: string,
  password?: string
): Promise<AgentRegistryLinkResponse> => {
  const response = await AgentRegistryLink.request(nats.cloudConnection(), {
    agentId: toIdFromHex(agentId)!,
    requestId: toIdFromHex(requestId)!,
    roomId: toIdFromHex(roomId)!,
    token,
    ...(username && { username }),
    ...(password && { password })
  });

  return response;
};

/**
 * Directly link an agent to a room via the mDNS discovery service.
 *
 * @returns The response from the link request containing requestId, bridgeId and token
 */
export const directLinkStatusAndRequestAgent = async ({
  service,
  bridgeLinkingRequestId,
  username,
  password,
  user,
  studioName,
  isPreApprovedForUnlink
}: {
  service: DiscoveredService | T_Maybe<Discovery>;
  bridgeLinkingRequestId?: string;
  username?: string;
  password?: string;
  user?: Pick<User, 'email' | 'firstName' | 'lastName' | 'id'>;
  studioName?: string;
  isPreApprovedForUnlink?: boolean;
}): Promise<{
  agentDirectStatus: AgentDirectLinkStatusResponse;
  agentDirectLinkDetails: AgentDirectLinkingResponse;
}> => {
  const response = await ElectronAPI.mDNS.proxyDirectStatusAndLinkRequest({
    service,
    ...(bridgeLinkingRequestId && { bridgeLinkingRequestId }),
    ...(isPreApprovedForUnlink && { isPreApprovedForUnlink }),
    ...(username && { username }),
    ...(password && { password }),
    ...(user && { user }),
    ...(studioName && { studioName })
  });

  return {
    agentDirectStatus: response.agentDirectStatus,
    agentDirectLinkDetails: response.agentDirectLinkDetails
  };
};

/**
 * Unlink an agent.
 *
 * @param agentId - Agent ID.
 * @param token - Access token.
 */
export const unlinkAgent = async (agentId: Agent['id'], token: string): Promise<void> => {
  await wrapApiCall(async () =>
    AgentRegistryUnlink.request(nats.cloudConnection(), {
      id: toIdFromHex(agentId)!,
      token
    })
  );
};

/**
 * Returns the set of agents associated with the current account.
 *
 * @param token - Access token.
 * @returns List of agents IDs or an empty array.
 */
export const listAgents = async (token: string): Promise<Agent['id'][]> => {
  const result = await wrapApiCall(async () =>
    AgentRegistryList.request(nats.cloudConnection(), {
      token
    })
  );

  return transformArray(result.ids, {
    transformer: (µ) => toHexFromId<Agent['id']>(µ)
  });
};

/**
 * Returns the set of organizations associated with the current account.
 *
 * @param token - Access token.
 * @returns List of organizations.
 */
export const listOrganizations = async (
  token: string,
  options?: { fresh?: boolean }
): Promise<AgentRegistryGetOrganizationResponse[]> => {
  // Idempotent read — transport transient retry (the SINGLE retry layer; the
  // redundant synchronizer call-site pRetry was removed, commit 7ed0b1e92).
  const result = await withTransientRetry(async () =>
    wrapApiCall(async () =>
      AgentRegistryListOrganizations.request(
        nats.cloudConnection(),
        { token },
        // Read-after-write recovery must cross the requester's ten-second
        // cache, including its stale-on-error fallback. Ordinary reads still
        // coalesce/cache, and transient retries remain owned by this layer.
        options?.fresh ? { force: true, staleOnError: false } : undefined
      )
    )
  );

  return result.organizations;
};

/**
 * Returns the set of rooms associated with the current account.
 *
 * @param token - Access token.
 * @param organizationId - Organization ID.
 * @param options - `fresh` reads through the requester's ten-second cache, for
 *   read-after-write callers (see `listOrganizations`).
 * @returns List of room IDs or an empty array.
 */
export const listRooms = async (
  token: string,
  organizationId?: Uint8Array,
  options?: { readonly fresh?: boolean }
): Promise<AgentRegistryGetRoomResponse[]> => {
  // Idempotent read — transport transient retry (the SINGLE retry layer; the
  // redundant synchronizer call-site pRetry was removed, commit 7ed0b1e92).
  const result = await withTransientRetry(async () =>
    wrapApiCall(async () =>
      AgentRegistryListRooms.request(
        nats.cloudConnection(),
        {
          organizationId,
          token
        },
        options?.fresh ? { force: true, staleOnError: false } : undefined
      )
    )
  );

  return result.rooms;
};

/**
 * Update the information about the linked agent..
 *
 * @param agentId - Agent ID.
 * @param updates - Agent updates.
 * @param token - Authentication token.
 */
export const updateAgent = async (
  agentId: Agent['id'],
  updates: T_Optional<Omit<Agent, 'id'>>,
  token: string
): Promise<void> => {
  await wrapApiCall(async () =>
    AgentRegistryUpdate.request(nats.cloudConnection(), {
      id: toIdFromHex(agentId)!,
      ...updates,
      token
    })
  );
};

export const loadMembers = async (
  roomIdHex: Room['id'],
  organizationIdHex: Organization['id'],
  token: string
): Promise<{
  members: AgentRegistryMembership[];
  invites: AgentRegistryCreateInviteResponse[];
}> => {
  const roomId = toIdFromHex(roomIdHex)!;
  const organizationId = toIdFromHex(organizationIdHex)!;

  const roomMembersPromise = AgentRegistryListMemberships.request(nats.cloudConnection(), {
    token,
    organizationId,
    roomId
  });

  const roomInvitesPromise = AgentRegistryListInvites.request(nats.cloudConnection(), {
    token,
    organizationId,
    roomId
  });

  const orgInvitesPromise = AgentRegistryListInvites.request(nats.cloudConnection(), {
    token,
    organizationId
  });

  const [roomMembersResp, roomInvitesResp, organizationInvitesResp] = await Promise.all([
    roomMembersPromise,
    roomInvitesPromise,
    orgInvitesPromise
  ]);

  return {
    // The room endpoint already includes inherited organization memberships.
    members: roomMembersResp.memberships,
    invites: organizationInvitesResp.invites.concat(roomInvitesResp.invites)
  };
};
